import { useSystem } from '@/context/SystemProvider'
import { Box } from '@chakra-ui/react'
import { motion, useMotionValue, useMotionValueEvent, useTransform } from 'framer-motion'
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
  a,
  t,
  v,
  s,
  getNextUniformRectilinearMotionValue,
  getUniformRetardedMotionValue,
  getX,
  getY,
  getZ,
} from '../../../helpers/motions'

enum TrackStage {
  Idle,
  Rolling,
  PreBraking,
  Breaking,
  DelayedBreaking,
}

type TrackState = {
  stage: TrackStage
  stopTimestamp: number | null
  currentTime: number | null
  indexToStop: number | null
  startTimestamp: number | null

  startBreakingPosition?: number
  stopBreakingPosition?: number
}

const initialState = {
  stage: TrackStage.Idle,
  stopTimestamp: null,
  currentTime: null,
  indexToStop: null,
  startTimestamp: null,
}

interface TrackProps {
  width: number
  elements: Array<any>
  renderElement: (data: any) => React.ReactElement
  elementWidth: number
  initialPosition: number
  roundFinishedCallback?: (index: number) => void
  panelChangeCallback?: () => void
  isTrackInitialized: boolean
  setTrackInitialized: () => void
}

export interface TrackController {
  start(): void
  stopIndex(stopTimestamp: number, index: number): void
  startStopIndex(stopTimestamp: number, index: number): void
}

export const Track = memo(
  forwardRef<TrackController, TrackProps>(
    (
      {
        width,
        elements,
        renderElement,
        elementWidth,
        initialPosition,
        roundFinishedCallback,
        panelChangeCallback,
        isTrackInitialized,
        setTrackInitialized,
      },
      ref
    ) => {
      const getCurrentDateTime = useSystem((state) => state.getCurrentDateTime)
      const [drawElement, setDrawElement] = useState<number | null>(null)
      let state = useRef<TrackState>(initialState)
      const baseX = useMotionValue(0)
      const x = useTransform(baseX, [0, elements.length], [0, -elements.length * elementWidth], {
        clamp: false,
      })

      const panel = useTransform(() => Math.round(parseFloat(baseX.get().toFixed(1))))

      useMotionValueEvent(panel, 'change', () => {
        panelChangeCallback?.()
      })

      const handleRoundFinish = (index: number) => {
        roundFinishedCallback?.(index)
        setDrawElement(index)
      }

      const setBaseX = (value: number) => {
        baseX.set(value % elements.length)
      }

      const animationOffset = useRef(0)
      const endRollingTimestamp = useRef(0)
      const startBreakingTimestamp = useRef(0)
      const startBreakingPosition = useRef(0)
      const breakingOffset = useRef(0)
      const distanceToStartBreaking = useRef(0)
      const delayedBreakingOffset = useRef(0)
      const previousPosition = useRef(initialPosition)
      const panelOffset = elements.length - width / elementWidth / 2 + 0.5
      const requestRef = useRef(0)
      const offset = useRef(0)

      useImperativeHandle(ref, () => {
        return {
          start() {
            setDrawElement(null)
            state.current = {
              ...state.current,
              stage: TrackStage.Rolling,
            }
          },
          stopIndex(stopTimestamp: number, index: number) {
            state.current = {
              ...state.current,
              stage: TrackStage.PreBraking,
              indexToStop: index,
              stopTimestamp,
            }

            startBreakingPosition.current = 0
            startBreakingTimestamp.current = 0
            distanceToStartBreaking.current = 0
          },
          startStopIndex(stopTimestamp: number, index: number) {
            state.current = {
              ...state.current,
              indexToStop: index,
              stage: TrackStage.DelayedBreaking,
              stopTimestamp,
            }

            breakingOffset.current = Math.max(getCurrentDateTime().getTime() - stopTimestamp, 0)
            startBreakingPosition.current = 0
            startBreakingTimestamp.current = 0
          },
        }
      }, [])

      useEffect(() => {
        previousPosition.current = initialPosition
        offset.current = (previousPosition.current + panelOffset) % elements.length
      }, [initialPosition])

      const animate = useCallback((time: number) => {
        switch (state.current.stage) {
          case TrackStage.Idle: {
            animationOffset.current = 0
            const currentPosition = panelOffset + previousPosition.current
            setBaseX(currentPosition)
            delayedBreakingOffset.current = 0
            break
          }
          case TrackStage.Rolling: {
            if (animationOffset.current === 0) {
              animationOffset.current = time
              endRollingTimestamp.current = 0
            }

            setBaseX(getNextUniformRectilinearMotionValue(time - animationOffset.current, offset.current))
            break
          }
          case TrackStage.PreBraking: {
            if (endRollingTimestamp.current === 0) {
              endRollingTimestamp.current = time
            }

            if (distanceToStartBreaking.current === 0) {
              distanceToStartBreaking.current = getX(
                state.current.indexToStop!,
                endRollingTimestamp.current,
                animationOffset.current,
                previousPosition.current,
                elements.length
              )
            }

            const currentPosition = getNextUniformRectilinearMotionValue(time - animationOffset.current, offset.current)
            setBaseX(currentPosition)

            const timeToStartBreaking = (distanceToStartBreaking.current / v) * 1000

            if (time < endRollingTimestamp.current + timeToStartBreaking) {
              break
            }

            state.current.stage = TrackStage.Breaking
            startBreakingTimestamp.current = endRollingTimestamp.current + timeToStartBreaking
            break
          }
          case TrackStage.Breaking: {
            if (startBreakingPosition.current === 0) {
              startBreakingPosition.current = getZ(
                startBreakingTimestamp.current - animationOffset.current,
                offset.current
              )
            }

            const currentPosition = getUniformRetardedMotionValue(
              time,
              startBreakingTimestamp.current,
              startBreakingPosition.current
            )

            if (startBreakingTimestamp.current + t * 1000 < time) {
              previousPosition.current = state.current.indexToStop!
              handleRoundFinish(state.current.indexToStop!)
              state.current.stage = TrackStage.Idle
            }

            setBaseX(currentPosition)
            break
          }
          case TrackStage.DelayedBreaking: {
            if (delayedBreakingOffset.current === 0) {
              delayedBreakingOffset.current = time
            }

            const startBreakingPoint = getY(state.current.indexToStop!, 0, 0, offset.current, s, elements.length)

            const passedBreakingTime = Math.min(
              (time - delayedBreakingOffset.current + breakingOffset.current) / 1_000,
              t
            )

            let moveBy = v * passedBreakingTime - (a * (passedBreakingTime * passedBreakingTime)) / 2

            setBaseX(startBreakingPoint + moveBy - 7)

            if (passedBreakingTime === t) {
              state.current.stage = TrackStage.Idle
              previousPosition.current = state.current.indexToStop!
              handleRoundFinish(state.current.indexToStop!)
            }

            break
          }
        }

        if (requestRef.current === 0) {
          setTrackInitialized()
        }

        requestRef.current = window?.requestAnimationFrame(animate)
      }, [])

      useEffect(() => {
        window?.requestAnimationFrame(animate)

        return () => {
          window.cancelAnimationFrame(requestRef.current)
        }
      }, [])

      useEffect(() => {
        document.addEventListener('visibilitychange', forceDelayedBreaking)
        return () => {
          document.removeEventListener('visibilitychange', forceDelayedBreaking)
        }
      }, [])

      const forceDelayedBreaking = () => {
        if (!document.hidden && [TrackStage.Breaking, TrackStage.PreBraking].includes(state.current.stage)) {
          breakingOffset.current = Math.max(getCurrentDateTime().getTime() - state.current.stopTimestamp!, 0)
          startBreakingTimestamp.current = 0
          delayedBreakingOffset.current = 0
          state.current.stage = TrackStage.DelayedBreaking
        }
      }

      if (!isTrackInitialized) {
        return null
      }

      return (
        <Box w={width + 'px'} overflow={'hidden'}>
          <motion.div style={{ x, display: 'flex', willChange: 'transform' }}>
            <TrackList
              drawElement={drawElement}
              elements={elements}
              renderElement={renderElement}
              elementWidth={elementWidth}
            />
          </motion.div>
        </Box>
      )
    }
  )
)

const TrackList = memo(({ elements, renderElement, elementWidth, drawElement }: any) => {
  return Array.from({ length: 3 }, () => {
    return elements.map((element: any, i: number) => (
      <Box key={i} flexShrink={0} width={elementWidth + 'px'}>
        {renderElement({ field: element, index: i, isDraw: i === drawElement })}
      </Box>
    ))
  })
})
